import { useEffect, useMemo } from 'react';
import {
  ensureE2eConnectorRecoveryCheckpointSeeded,
  ensureE2eSetupRevisionOpened,
  resolveE2eConnectorTargetTransport,
} from '../../../config/e2eConnectorRecoveryFixture';
import { type UseParameterAdditionReturn } from './models';
import { useAdditionalCategoryParameterAddition } from './useAdditionalCategoryParameterAddition';
import { useConnectorParameterAddition } from './useConnectorParameterAddition';
import { useConnectorTargetTransaction } from './useConnectorTargetTransaction';
import { useMappedProjectParameterAddition } from './useMappedProjectParameterAddition';
import { useParameterAdditionCore } from './useParameterAdditionCore';
import { useParameterMutationActions } from './useParameterMutationActions';
import { useParameterUndoActions } from './useParameterUndoActions';
import { useProcessProjectParameterAddition } from './useProcessProjectParameterAddition';

export function useParameterAddition(): UseParameterAdditionReturn {
  const core = useParameterAdditionCore();
  const mutations = useParameterMutationActions(core);
  const {
    undoLastConnectorParameterAddition,
    undoLastAdditionalCategoryParameterAddition,
  } = useParameterUndoActions(core);
  const addParameters = useConnectorParameterAddition(core, mutations);
  const addAdditionalCategoryParameters = useAdditionalCategoryParameterAddition(core, mutations);
  const addProcessAndProjectParameters = useProcessProjectParameterAddition(
    core,
    mutations,
  );
  const addMappedProjectParameters = useMappedProjectParameterAddition(
    core,
    mutations,
  );
  // Outside the screenshot seam this is the untouched pair of Revit calls.
  const connectorTransport = useMemo(
    () =>
      resolveE2eConnectorTargetTransport({
        addParameters,
        undoLastConnectorParameterAddition,
      }),
    [addParameters, undoLastConnectorParameterAddition]
  );
  useEffect(() => {
    // Order matters only for readability: neither the transition decision nor
    // the setup fingerprint reads the revision id, so the seeded checkpoint
    // survives the revision the reopen bumps.
    ensureE2eSetupRevisionOpened();
    ensureE2eConnectorRecoveryCheckpointSeeded();
  }, []);

  const { applyConnectorTarget } = useConnectorTargetTransaction({
    projectKey: core.currentProjectUndoKey,
    documentName: core.activeDocumentInfo.documentName,
    documentPath: core.activeDocumentInfo.documentPath,
    addParameters: connectorTransport.addParameters,
    undoLastConnectorParameterAddition: connectorTransport.undoLastConnectorParameterAddition,
  });

  return {
    isAdding: core.isAdding,
    parameterProgress: core.parameterProgress,
    activeConnectorOperation: core.activeConnectorOperation,
    connectorSelectionBlockedByMismatch: core.connectorSelectionBlockedByMismatch,
    addParameters,
    applyConnectorTarget,
    undoLastConnectorParameterAddition,
    lastConnectorUndoPlan: core.lastConnectorUndoPlan,
    addAdditionalCategoryParameters,
    undoLastAdditionalCategoryParameterAddition,
    lastAdditionalCategoryUndoPlan: core.lastAdditionalCategoryUndoPlan,
    addProcessAndProjectParameters,
    addMappedProjectParameters,
    getConfiguredParameters: core.getConfiguredParameters,
  };
}

export default useParameterAddition;
